import { useEffect, useState, useCallback, useMemo } from "react";
import "@/App.css";
import axios from "axios";
import {
  Cake, HeartHandshake, Users, Mail, Settings as SettingsIcon, Activity,
  Send, RefreshCw, Upload, Trash2, Plus, Eye, Download, CalendarDays,
  CheckCircle2, AlertCircle, ImageIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "today", label: "Today", icon: Cake },
  { id: "upcoming", label: "Upcoming", icon: CalendarDays },
  { id: "members", label: "Members", icon: Users },
  { id: "history", label: "History", icon: Activity },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const formatDate = (m, d, y) => {
  if (!m || !d) return "—";
  const suf = (n) => {
    if (n >= 11 && n <= 13) return "th";
    return { 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th";
  };
  return `${d}${suf(d)} ${MONTHS[m - 1]}${y ? " " + y : ""}`;
};

function Brand() {
  return (
    <div className="flex items-center gap-3" data-testid="app-brand">
      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#c79b27] to-[#8a6a14] flex items-center justify-center text-white font-bold shadow-lg ring-2 ring-[#c79b27]/30">
        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
          <circle cx="12" cy="12" r="3" />
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" fill="none" />
          {[...Array(8)].map((_, i) => {
            const a = (i * Math.PI) / 4;
            const x1 = 12 + Math.cos(a) * 4;
            const y1 = 12 + Math.sin(a) * 4;
            const x2 = 12 + Math.cos(a) * 8;
            const y2 = 12 + Math.sin(a) * 8;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" strokeWidth="1.5" />;
          })}
        </svg>
      </div>
      <div>
        <p className="text-[11px] tracking-[0.22em] uppercase text-amber-700 font-semibold">RID 3240 · Zone VI</p>
        <h1 className="text-xl font-bold text-slate-900 leading-tight">Rotary Tezpur · Greetings</h1>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint, tone = "amber" }) {
  const tones = {
    amber: "from-amber-50 to-amber-100/40 text-amber-700 ring-amber-200",
    rose: "from-rose-50 to-rose-100/40 text-rose-700 ring-rose-200",
    emerald: "from-emerald-50 to-emerald-100/40 text-emerald-700 ring-emerald-200",
    indigo: "from-indigo-50 to-indigo-100/40 text-indigo-700 ring-indigo-200",
  };
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${tones[tone]} p-5 ring-1 shadow-sm`} data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider opacity-80">{label}</p>
          <p className="mt-1 text-3xl font-bold text-slate-900">{value}</p>
          {hint && <p className="mt-1 text-xs text-slate-600">{hint}</p>}
        </div>
        <Icon className="w-7 h-7 opacity-70" />
      </div>
    </div>
  );
}

function CardPreview({ src, alt = "card", testid = "card-preview" }) {
  return (
    <div className="rounded-xl overflow-hidden border border-stone-200 bg-stone-50 shadow-sm" data-testid={testid}>
      <img src={src} alt={alt} className="w-full h-auto block" />
    </div>
  );
}

// =================================================================
// Today Tab
// =================================================================
function TodayTab({ refreshTick }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [previewIdx, setPreviewIdx] = useState(null);
  const [regenIdx, setRegenIdx] = useState(null);
  const [quote, setQuote] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await axios.get(`${API}/events/today`);
    setEvents(data.events);
    if (data.events.length === 0) {
      try {
        const q = await axios.get(`${API}/cards/quote/text`);
        setQuote(q.data);
      } catch {
        setQuote(null);
      }
    } else {
      setQuote(null);
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load, refreshTick]);

  const runNow = async () => {
    setRunning(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/events/run-now`);
      setResult(data);
      await load();
    } catch (e) {
      setResult({ error: e.message });
    } finally {
      setRunning(false);
    }
  };

  const regen = async (e, idx) => {
    setRegenIdx(idx);
    try {
      await axios.post(`${API}/members/${e.member_id}/card/regenerate?kind=${e.kind}`);
      setPreviewIdx(idx); // refresh preview
    } finally {
      setRegenIdx(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="today-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Today's Greetings</h2>
          <p className="text-sm text-slate-600 mt-1">
            Auto-send fires daily at <span className="font-mono">00:30 UTC</span> (06:00 IST). On days with no birthdays/anniversaries a <em>Thought of the Day</em> card is sent instead.
          </p>
        </div>
        <button
          onClick={runNow}
          disabled={running}
          data-testid="run-now-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 active:scale-95 transition disabled:opacity-60"
        >
          {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {running ? "Sending…" : "Run Now"}
        </button>
      </div>

      {result && (
        <div data-testid="run-result" className={`rounded-xl border p-4 text-sm ${result.error ? "border-rose-300 bg-rose-50 text-rose-800" : "border-emerald-300 bg-emerald-50 text-emerald-900"}`}>
          {result.error
            ? <>Failed: {result.error}</>
            : <>Processed <b>{result.events_processed}</b> event(s){result.sent_quote ? <> + 1 quote card</> : null} at {new Date(result.ran_at).toLocaleString()}.</>}
        </div>
      )}

      {loading ? <p className="text-slate-500">Loading…</p> :
        events.length === 0 ? (
          <div data-testid="quote-section" className="space-y-4">
            <div className="rounded-2xl bg-gradient-to-br from-stone-50 to-amber-50/30 border border-amber-200 p-6">
              <p className="text-[11px] tracking-[0.22em] uppercase text-amber-700 font-bold">Thought of the Day</p>
              <p className="mt-3 text-lg italic text-slate-800 leading-relaxed" data-testid="quote-text">
                {quote ? `“${quote.quote}”` : "Generating today's thought…"}
              </p>
              <p className="mt-3 text-xs text-slate-500">
                No birthdays or anniversaries today. This quote card will be sent at 06:00 IST instead.
              </p>
            </div>
            <CardPreview src={`${API}/cards/quote?ts=${refreshTick}`} testid="quote-card-preview" alt="quote card" />
            <button
              onClick={async () => {
                await axios.get(`${API}/cards/quote?force_new_bg=true&force_new_quote=true`);
                load();
              }}
              data-testid="regen-quote-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-stone-300 text-sm font-semibold text-slate-700 hover:bg-stone-50">
              <RefreshCw className="w-4 h-4" /> Regenerate Thought & artwork
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {events.map((e, idx) => (
              <div key={idx} className="rounded-2xl bg-white border border-stone-200 p-5 shadow-sm hover:shadow-md transition" data-testid={`event-card-${idx}`}>
                <div className="flex items-center gap-2">
                  {e.kind === "birthday"
                    ? <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold"><Cake className="w-3.5 h-3.5" /> Birthday</span>
                    : <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-100 text-rose-800 text-xs font-semibold"><HeartHandshake className="w-3.5 h-3.5" /> Anniversary</span>}
                </div>
                <h3 className="mt-3 text-lg font-bold text-slate-900">{e.name}</h3>
                {e.spouse_name && <p className="text-sm text-slate-600">& {e.spouse_name}</p>}
                <p className="mt-1 text-sm text-slate-500">{e.date}</p>
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={() => setPreviewIdx(idx)}
                    data-testid={`preview-btn-${idx}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-amber-700 hover:text-amber-900"
                  >
                    <Eye className="w-4 h-4" /> Preview
                  </button>
                  <button
                    onClick={() => regen(e, idx)}
                    disabled={regenIdx === idx}
                    data-testid={`regen-btn-${idx}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${regenIdx === idx ? "animate-spin" : ""}`} /> {regenIdx === idx ? "Regenerating…" : "Regenerate art"}
                  </button>
                </div>
                {previewIdx === idx && (
                  <div className="mt-4">
                    <CardPreview src={`${API}/members/${e.member_id}/card?kind=${e.kind}&ts=${regenIdx === idx ? "x" : Date.now()}`} testid={`preview-img-${idx}`} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// =================================================================
// Upcoming Tab
// =================================================================
function UpcomingTab({ refreshTick }) {
  const [data, setData] = useState({ events: [] });
  const [days, setDays] = useState(30);

  useEffect(() => {
    axios.get(`${API}/events/upcoming?days=${days}`).then((r) => setData(r.data));
  }, [days, refreshTick]);

  return (
    <div className="space-y-5" data-testid="upcoming-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Upcoming Events</h2>
          <p className="text-sm text-slate-600">Next {days} days of birthdays & anniversaries.</p>
        </div>
        <div className="flex gap-2">
          {[7, 30, 60, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`upcoming-range-${d}`}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold ${days === d ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-stone-300 hover:bg-stone-100"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {data.events.length === 0 ? (
        <p className="text-slate-500" data-testid="no-upcoming">No upcoming events in this range.</p>
      ) : (
        <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-700">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">In</th>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Type</th>
                <th className="text-left px-4 py-3 font-semibold">Member</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {data.events.map((e, i) => (
                <tr key={i} data-testid={`upcoming-row-${i}`} className="hover:bg-stone-50">
                  <td className="px-4 py-3 font-mono text-slate-900">
                    {e.days_until === 0 ? <span className="text-emerald-700 font-semibold">Today</span>
                      : e.days_until === 1 ? "Tomorrow"
                      : `${e.days_until} days`}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{e.date}</td>
                  <td className="px-4 py-3">
                    {e.kind === "birthday"
                      ? <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold">Birthday</span>
                      : <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-800 text-xs font-semibold">Anniversary</span>}
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {e.name}{e.spouse_name && <span className="text-slate-500 font-normal"> & {e.spouse_name}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =================================================================
// Members Tab (CRUD + import + preview)
// =================================================================
const EMPTY_MEMBER = {
  full_name: "", email: "", phone: "",
  dob_month: "", dob_day: "", dob_year: "",
  spouse_name: "",
  anniv_month: "", anniv_day: "", anniv_year: "",
  active: true,
};

function MembersTab({ onChange }) {
  const [members, setMembers] = useState([]);
  const [filter, setFilter] = useState("");
  const [editor, setEditor] = useState(null); // {mode: 'create'|'edit', data}
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/members`);
    setMembers(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = filter.toLowerCase().trim();
    if (!q) return members;
    return members.filter(m =>
      m.full_name.toLowerCase().includes(q) ||
      (m.email || "").toLowerCase().includes(q) ||
      (m.spouse_name || "").toLowerCase().includes(q)
    );
  }, [members, filter]);

  const save = async () => {
    setBusy(true);
    const payload = { ...editor.data };
    ["dob_month", "dob_day", "dob_year", "anniv_month", "anniv_day", "anniv_year"].forEach(k => {
      if (payload[k] === "" || payload[k] === null) payload[k] = null;
      else payload[k] = parseInt(payload[k], 10);
    });
    Object.keys(payload).forEach(k => {
      if (typeof payload[k] === "string" && payload[k].trim() === "") payload[k] = null;
    });
    try {
      if (editor.mode === "create") {
        await axios.post(`${API}/members`, payload);
      } else {
        await axios.put(`${API}/members/${editor.data.id}`, payload);
      }
      setEditor(null);
      await load();
      onChange?.();
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.full_name}?`)) return;
    await axios.delete(`${API}/members/${m.id}`);
    await load();
    onChange?.();
  };

  const importFile = async (file) => {
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/members/import`, fd);
      alert(`Imported ${data.inserted} new, updated ${data.updated} existing.`);
      await load();
      onChange?.();
    } catch (e) {
      alert("Import failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="members-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Members <span className="text-slate-400 font-normal text-base">· {members.length}</span></h2>
          <p className="text-sm text-slate-600">Manage the directory used for daily greetings.</p>
        </div>
        <div className="flex gap-2">
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-stone-300 text-sm font-semibold text-slate-700 hover:bg-stone-50 cursor-pointer" data-testid="import-btn">
            <Upload className="w-4 h-4" /> {importing ? "Importing…" : "Import XLSX"}
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => e.target.files[0] && importFile(e.target.files[0])} />
          </label>
          <button onClick={() => setEditor({ mode: "create", data: { ...EMPTY_MEMBER } })}
            data-testid="add-member-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 active:scale-95 transition">
            <Plus className="w-4 h-4" /> Add Member
          </button>
        </div>
      </div>

      <input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Search by name, email or spouse…"
        data-testid="member-search"
        className="w-full px-4 py-2.5 rounded-full bg-white border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
      />

      <div className="rounded-2xl border border-stone-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-stone-50 text-stone-700">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Name</th>
              <th className="text-left px-4 py-3 font-semibold">Birthday</th>
              <th className="text-left px-4 py-3 font-semibold">Anniversary</th>
              <th className="text-left px-4 py-3 font-semibold">Email</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {filtered.map((m) => (
              <tr key={m.id} className="hover:bg-stone-50" data-testid={`member-row-${m.id}`}>
                <td className="px-4 py-3 font-medium text-slate-900">
                  {m.full_name}
                  {m.spouse_name && <span className="block text-xs text-slate-500">& {m.spouse_name}</span>}
                </td>
                <td className="px-4 py-3 text-slate-700">{formatDate(m.dob_month, m.dob_day)}</td>
                <td className="px-4 py-3 text-slate-700">{formatDate(m.anniv_month, m.anniv_day, m.anniv_year)}</td>
                <td className="px-4 py-3 text-slate-600 text-xs">{m.email || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    {m.dob_month && (
                      <button onClick={() => setPreview({ id: m.id, kind: "birthday", name: m.full_name })}
                        title="Preview birthday card"
                        data-testid={`preview-bday-${m.id}`}
                        className="p-1.5 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100">
                        <Cake className="w-4 h-4" />
                      </button>
                    )}
                    {m.anniv_month && (
                      <button onClick={() => setPreview({ id: m.id, kind: "anniversary", name: m.full_name })}
                        title="Preview anniversary card"
                        data-testid={`preview-anniv-${m.id}`}
                        className="p-1.5 rounded-full bg-rose-50 text-rose-700 hover:bg-rose-100">
                        <HeartHandshake className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => setEditor({ mode: "edit", data: { ...m } })}
                      data-testid={`edit-member-${m.id}`}
                      className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold hover:bg-slate-200">
                      Edit
                    </button>
                    <button onClick={() => remove(m)} data-testid={`delete-member-${m.id}`}
                      className="p-1.5 rounded-full bg-rose-50 text-rose-700 hover:bg-rose-100">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editor && (
        <MemberEditor
          editor={editor}
          setEditor={setEditor}
          save={save}
          busy={busy}
        />
      )}
      {preview && (
        <Modal onClose={() => setPreview(null)} title={`${preview.kind === "birthday" ? "Birthday" : "Anniversary"} card · ${preview.name}`}>
          <CardPreview src={`${API}/members/${preview.id}/card?kind=${preview.kind}`} testid="modal-card-preview" />
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="modal-content">
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200">
          <h3 className="font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} data-testid="modal-close-btn" className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function MemberEditor({ editor, setEditor, save, busy }) {
  const set = (k, v) => setEditor({ ...editor, data: { ...editor.data, [k]: v } });
  const f = editor.data;
  return (
    <Modal onClose={() => setEditor(null)} title={editor.mode === "create" ? "New Member" : `Edit · ${f.full_name}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="member-editor">
        <Input label="Full Name *" value={f.full_name} onChange={(v) => set("full_name", v)} testid="editor-name" />
        <Input label="Email" type="email" value={f.email || ""} onChange={(v) => set("email", v)} testid="editor-email" />
        <Input label="Phone" value={f.phone || ""} onChange={(v) => set("phone", v)} testid="editor-phone" />
        <Input label="Spouse Name" value={f.spouse_name || ""} onChange={(v) => set("spouse_name", v)} testid="editor-spouse" />
        <div className="sm:col-span-2 grid grid-cols-3 gap-3">
          <Input label="DOB Month (1-12)" type="number" value={f.dob_month || ""} onChange={(v) => set("dob_month", v)} testid="editor-dob-month" />
          <Input label="DOB Day (1-31)" type="number" value={f.dob_day || ""} onChange={(v) => set("dob_day", v)} testid="editor-dob-day" />
          <Input label="DOB Year (opt)" type="number" value={f.dob_year || ""} onChange={(v) => set("dob_year", v)} testid="editor-dob-year" />
        </div>
        <div className="sm:col-span-2 grid grid-cols-3 gap-3">
          <Input label="Anniv Month" type="number" value={f.anniv_month || ""} onChange={(v) => set("anniv_month", v)} testid="editor-anniv-month" />
          <Input label="Anniv Day" type="number" value={f.anniv_day || ""} onChange={(v) => set("anniv_day", v)} testid="editor-anniv-day" />
          <Input label="Anniv Year" type="number" value={f.anniv_year || ""} onChange={(v) => set("anniv_year", v)} testid="editor-anniv-year" />
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={() => setEditor(null)} data-testid="editor-cancel-btn"
          className="px-4 py-2 rounded-full bg-stone-100 text-slate-700 text-sm font-semibold hover:bg-stone-200">Cancel</button>
        <button onClick={save} disabled={busy || !f.full_name?.trim()} data-testid="editor-save-btn"
          className="px-4 py-2 rounded-full bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-60">
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </Modal>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-slate-500 mb-1 font-semibold">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full px-3 py-2 rounded-lg bg-white border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
    </label>
  );
}

// =================================================================
// History Tab
// =================================================================
function HistoryTab({ refreshTick }) {
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    axios.get(`${API}/logs?limit=200`).then((r) => setLogs(r.data.logs));
  }, [refreshTick]);

  return (
    <div className="space-y-5" data-testid="history-tab">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Greeting History</h2>
        <p className="text-sm text-slate-600">Last 200 deliveries.</p>
      </div>
      {logs.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-stone-300 bg-stone-50 p-12 text-center" data-testid="no-history">
          <ImageIcon className="w-10 h-10 mx-auto text-stone-400" />
          <p className="mt-4 text-slate-700 font-medium">No greetings sent yet.</p>
          <p className="text-sm text-slate-500 mt-1">Greetings will appear here automatically after each send.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-700">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Sent</th>
                <th className="text-left px-4 py-3 font-semibold">Type</th>
                <th className="text-left px-4 py-3 font-semibold">Member</th>
                <th className="text-left px-4 py-3 font-semibold">Event</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Card</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {logs.map((l) => (
                <tr key={l.id} data-testid={`log-row-${l.id}`} className="hover:bg-stone-50">
                  <td className="px-4 py-3 text-slate-700">{new Date(l.sent_at).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    {l.kind === "birthday"
                      ? <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold">Birthday</span>
                      : l.kind === "anniversary"
                      ? <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-800 text-xs font-semibold">Anniversary</span>
                      : <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 text-xs font-semibold">Quote</span>}
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">{l.member_name}</td>
                  <td className="px-4 py-3 text-slate-700">{l.event_date}</td>
                  <td className="px-4 py-3">
                    {l.status === "sent" ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> Sent</span>
                    ) : l.status === "failed" ? (
                      <span className="inline-flex items-center gap-1 text-rose-700 font-semibold text-xs" title={l.error || ""}><AlertCircle className="w-3.5 h-3.5" /> Failed</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-slate-600 font-semibold text-xs">Preview</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <a href={`${API}/logs/${l.id}/card`} target="_blank" rel="noreferrer"
                      data-testid={`log-card-${l.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold hover:bg-slate-200">
                      <Download className="w-3.5 h-3.5" /> PNG
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =================================================================
// Settings Tab
// =================================================================
function SettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => setS(r.data));
  }, []);
  if (!s) return <p className="text-slate-500">Loading…</p>;

  const save = async () => {
    setSaving(true);
    setSavedMsg("");
    try {
      await axios.put(`${API}/settings`, s);
      setSavedMsg("Settings saved.");
    } catch (e) {
      setSavedMsg("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
      setTimeout(() => setSavedMsg(""), 4000);
    }
  };

  return (
    <div className="space-y-5 max-w-2xl" data-testid="settings-tab">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Settings</h2>
        <p className="text-sm text-slate-600">Configure delivery & scheduling.</p>
      </div>

      <div className="rounded-2xl bg-white border border-stone-200 p-5 space-y-4">
        <Input label="Recipient Email" value={s.recipient_email} onChange={(v) => setS({ ...s, recipient_email: v })} testid="settings-recipient" />
        <Input label="Sender Email (verified Resend domain)" value={s.sender_email} onChange={(v) => setS({ ...s, sender_email: v })} testid="settings-sender" />
        <Input label="Sender Name" value={s.sender_name} onChange={(v) => setS({ ...s, sender_name: v })} testid="settings-sender-name" />
        <label className="flex items-center gap-3" data-testid="settings-autosend-row">
          <input type="checkbox" checked={s.auto_send} onChange={(e) => setS({ ...s, auto_send: e.target.checked })} className="w-5 h-5 rounded text-amber-600" data-testid="settings-autosend" />
          <span className="text-sm text-slate-700">Auto-send greetings on schedule (otherwise dry-run only)</span>
        </label>
      </div>

      <div className="rounded-2xl bg-amber-50/60 border border-amber-200 p-5 text-sm text-slate-700 space-y-1.5">
        <p className="font-bold text-amber-900">Schedule</p>
        <p>Last run: <span className="font-mono">{s.last_run_at ? new Date(s.last_run_at).toLocaleString() : "never"}</span></p>
        <p>Next run: <span className="font-mono">{s.next_run_at ? new Date(s.next_run_at).toLocaleString() : "—"}</span></p>
        <p className="text-xs text-slate-500 mt-2">Fires daily at 00:30 UTC = 06:00 IST.</p>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving} data-testid="save-settings-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-600 text-white font-semibold hover:bg-amber-700 disabled:opacity-60">
          {saving ? "Saving…" : "Save"}
        </button>
        {savedMsg && <span className="text-sm text-emerald-700 font-medium" data-testid="settings-saved-msg">{savedMsg}</span>}
      </div>
    </div>
  );
}

// =================================================================
// Root App
// =================================================================
function App() {
  const [tab, setTab] = useState("today");
  const [tick, setTick] = useState(0);
  const [meta, setMeta] = useState(null);
  const [counts, setCounts] = useState({ members: 0, today: 0, upcoming30: 0, sent: 0 });

  const reload = useCallback(async () => {
    const [m, t, u, l, s] = await Promise.all([
      axios.get(`${API}/members`),
      axios.get(`${API}/events/today`),
      axios.get(`${API}/events/upcoming?days=30`),
      axios.get(`${API}/logs?limit=500`),
      axios.get(`${API}/`),
    ]);
    setCounts({
      members: m.data.length,
      today: t.data.count,
      upcoming30: u.data.events.length,
      sent: l.data.logs.filter((x) => x.status === "sent").length,
    });
    setMeta(s.data);
  }, []);
  useEffect(() => { reload(); }, [reload, tick]);

  const Tab = TABS.find((x) => x.id === tab);

  return (
    <div className="min-h-screen bg-[#f7f4ee]" data-testid="app-root">
      {/* decorative top stripe */}
      <div className="h-1 bg-gradient-to-r from-[#c79b27] via-[#e3c46b] to-[#c79b27]" />

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-6">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <Brand />
          {meta && (
            <p className="text-xs text-slate-600" data-testid="header-meta">
              Today (IST): <span className="font-mono font-semibold">{meta.today_ist}</span>
              {" · "}Recipient: <span className="font-mono">{meta.recipient}</span>
            </p>
          )}
        </header>

        {/* stat grid */}
        <section className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat icon={Users} label="Members" value={counts.members} tone="indigo" />
          <Stat icon={Cake} label="Today" value={counts.today} hint="Events firing today" tone="amber" />
          <Stat icon={CalendarDays} label="Next 30 days" value={counts.upcoming30} tone="emerald" />
          <Stat icon={Mail} label="Greetings sent" value={counts.sent} tone="rose" />
        </section>

        {/* tabs */}
        <nav className="mt-8 flex flex-wrap gap-2 border-b border-stone-200" data-testid="tab-nav">
          {TABS.map((t) => {
            const I = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`-mb-px inline-flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-semibold border-b-2 transition ${tab === t.id ? "border-amber-600 text-amber-700 bg-white" : "border-transparent text-slate-600 hover:text-slate-900"}`}>
                <I className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </nav>

        <main className="mt-6 pb-16">
          {tab === "today" && <TodayTab refreshTick={tick} />}
          {tab === "upcoming" && <UpcomingTab refreshTick={tick} />}
          {tab === "members" && <MembersTab onChange={() => setTick((t) => t + 1)} />}
          {tab === "history" && <HistoryTab refreshTick={tick} />}
          {tab === "settings" && <SettingsTab />}
        </main>
      </div>
    </div>
  );
}

export default App;
